import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, Loader2, CheckCircle2, ExternalLink, Receipt, FileText } from "lucide-react";
import parentApi from "@/lib/parentApi";
import { toast } from "sonner";
import { viewFromApi } from "@/components/FileViewer";

import { money } from "@/lib/money";

/** Parent view: tuition invoices with Stripe "Pay now"; polls the session after returning from checkout. */
export function StudentTuition({ studentId }) {
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState("");
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const [polling, setPolling] = useState(!!sessionId);

  const load = useCallback(async () => { const { data: d } = await parentApi.get(`/parent/students/${studentId}/invoices`); setData(d); }, [studentId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0; let stop = false;
    const tick = async () => {
      try {
        const { data: s } = await parentApi.get(`/parent/invoices/checkout/${sessionId}`);
        if (s.payment_status === "paid") { toast.success("Payment received — thank you!"); setPolling(false); load(); params.delete("session_id"); setParams(params, { replace: true }); return; }
        if (s.status === "expired") { toast.error("Checkout session expired"); setPolling(false); return; }
      } catch { /* keep polling */ }
      if (!stop && ++tries < 8) setTimeout(tick, 2500); else setPolling(false);
    };
    tick();
    return () => { stop = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async (inv) => {
    setPaying(inv.id);
    try {
      const { data: r } = await parentApi.post(`/parent/invoices/${inv.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start payment"); setPaying(""); }
  };

  if (!data) return <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>;
  const today = new Date().toISOString().slice(0, 10);
  const openDoc = (i, kind) => viewFromApi(parentApi, `/parent/invoices/${i.id}/${kind}.pdf`, { title: `${kind === "receipt" ? "Receipt" : "Invoice"} ${(kind === "receipt" ? i.receipt_number : i.invoice_number) || ""}`.trim(), filename: `${kind === "receipt" ? "Receipt" : "Invoice"}-${(kind === "receipt" ? i.receipt_number : i.invoice_number) || i.id}.pdf` });
  const openItems = data.items.filter((i) => i.status === "open");
  const paidItems = data.items.filter((i) => i.status === "paid");

  return (
    <div className="space-y-5" data-testid="student-tuition">
      {polling && <div className="flex items-center gap-2 rounded-xl bg-sky-50 px-4 py-3 text-sm text-sky-800" data-testid="tuition-polling"><Loader2 className="h-4 w-4 animate-spin" /> Confirming your payment…</div>}
      <div className="rounded-2xl bg-slate-900 p-5 text-white shadow-lg shadow-slate-900/10">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-400">Balance due</div>
            <div className="font-display text-3xl font-bold tabular-nums" data-testid="tuition-balance">{money(data.open_cents)}</div>
          </div>
          <span className="rounded-full bg-white/10 p-2.5"><CreditCard className="h-5 w-5 text-white" /></span>
        </div>
        <div className="mt-3 text-xs text-slate-300">{openItems.length ? `${openItems.length} open invoice${openItems.length === 1 ? "" : "s"}${openItems.some((i) => i.due_date < today) ? " · 1 or more overdue" : ""}` : "You're all caught up — thank you!"}</div>
      </div>
      {data.items.length === 0 && <div className="rounded-2xl border border-dashed border-slate-200 py-12 text-center text-sm text-slate-400" data-testid="tuition-empty">No invoices yet.</div>}

      {openItems.length > 0 && <Section title="Due">
        {openItems.map((i) => <InvoiceCard key={i.id} inv={i} today={today} openDoc={openDoc} action={
          <button onClick={() => pay(i)} disabled={!!paying} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition hover:shadow disabled:opacity-60" data-testid={`tuition-pay-${i.id}`}>
            {paying === i.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />} Pay now
          </button>} />)}
      </Section>}
      {paidItems.length > 0 && <Section title="Payment history">
        {paidItems.map((i) => <InvoiceCard key={i.id} inv={i} today={today} openDoc={openDoc} action={
          <button onClick={() => openDoc(i, "receipt")} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-50" data-testid={`tuition-receipt-${i.id}`}>
            <Receipt className="h-3.5 w-3.5" /> Receipt
          </button>} />)}
      </Section>}
    </div>
  );
}

const fmt = (iso, y = false) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", ...(y ? { year: "numeric" } : {}) }) : "");

function Section({ title, children }) {
  return <section><h3 className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-widest text-slate-500">{title}</h3><ul className="space-y-2">{children}</ul></section>;
}

function InvoiceCard({ inv: i, today, openDoc, action }) {
  const overdue = i.status === "open" && i.due_date < today;
  return (
    <li className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`tuition-invoice-${i.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-900 capitalize">{i.cadence} tuition</span>
            {i.status === "paid"
              ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200"><CheckCircle2 className="h-3 w-3" /> Paid</span>
              : <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${overdue ? "bg-red-50 text-red-700 ring-red-200" : "bg-amber-50 text-amber-800 ring-amber-200"}`}>{overdue ? "Overdue" : "Due " + fmt(i.due_date)}</span>}
          </div>
          <div className="mt-0.5 text-xs text-slate-500">{fmt(i.period_start)} – {fmt(i.period_end, true)}</div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
            {i.invoice_number && <button onClick={() => openDoc(i, "invoice")} className="inline-flex items-center gap-1 font-mono hover:text-slate-700 hover:underline" data-testid={`tuition-invoice-pdf-${i.id}`}><FileText className="h-3 w-3" /> {i.invoice_number}</button>}
            {i.receipt_number && <span className="font-mono">{i.receipt_number}</span>}
            {i.late_fee_cents ? <span className="text-red-600">includes {money(i.late_fee_cents)} late fee</span> : null}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <div className="font-display text-lg font-bold tabular-nums text-slate-900">{money(i.total_cents)}</div>
          {action}
        </div>
      </div>
    </li>
  );
}
